import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Text,
  HStack,
  Alert,
} from '@chakra-ui/react'
import { makeAuthenticatedRequest } from '../../utils/apiInterceptor'

export interface AccountDeletionStatus {
  status: 'none' | 'pending'
  requested_at?: string
  scheduled_for?: string
  grace_days?: number
}

export const fetchAccountDeletionStatus = async (): Promise<AccountDeletionStatus> => {
  return makeAuthenticatedRequest<AccountDeletionStatus>('/api/v1/users/account/deletion-status')
}

export const cancelAccountDeletion = async (): Promise<void> => {
  await makeAuthenticatedRequest('/api/v1/users/account/cancel-deletion', {
    method: 'POST',
  })
}

interface PendingDeletionBannerProps {
  onCancelled?: () => void
}

/** Shown when a signed-in user has a pending account deletion during the grace period. */
export const PendingDeletionBanner: React.FC<PendingDeletionBannerProps> = ({ onCancelled }) => {
  const [status, setStatus] = useState<AccountDeletionStatus | null>(null)
  const [cancelling, setCancelling] = useState(false)

  const loadStatus = useCallback(async () => {
    try {
      const next = await fetchAccountDeletionStatus()
      setStatus(next)
    } catch {
      setStatus(null)
    }
  }, [])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  if (!status || status.status !== 'pending') {
    return null
  }

  const scheduledDate = status.scheduled_for
    ? new Date(status.scheduled_for).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'the scheduled date'

  const handleCancel = async () => {
    setCancelling(true)
    try {
      await cancelAccountDeletion()
      setStatus({ status: 'none' })
      onCancelled?.()
    } finally {
      setCancelling(false)
    }
  }

  return (
    <Box px={4} pt={3}>
      <Alert.Root status="warning" borderRadius="md">
        <Alert.Indicator />
        <Alert.Content flex={1}>
          <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
            <Text fontSize="sm">
              Your account is scheduled for permanent deletion on {scheduledDate}. Sign in during
              the {status.grace_days ?? 30}-day grace period to cancel.
            </Text>
            <Button size="sm" variant="outline" onClick={handleCancel} loading={cancelling}>
              Cancel deletion
            </Button>
          </HStack>
        </Alert.Content>
      </Alert.Root>
    </Box>
  )
}

export default PendingDeletionBanner
